import { HubConnectionViewObject, UserRole, UserViewObject } from '@/api';
import { Ref, computed, onBeforeUnmount, ref, watch } from 'vue';
import { userStore } from '@/core/store/user/user.store';
import { mergeObject } from '@/core/util/mergeObjects';
import userService from '@/core/bulk/services/userBulkFetch';
import usersApiService from '@/core/api/controllers/usersApi.service';
import bus from '@/core/bus';

export function useUser(userId: Ref<string | null | undefined>, onUpdate?: () => void) {
    const user = ref<UserViewObject | null>(null);

    const addOrUpdateUser = (updated?: UserViewObject) => {
        if (updated && updated.id === userId.value) {
            user.value = updated;
            onUpdate?.();
        }
    };

    const addOrUpdateUsers = (data: UserViewObject[]) => {
        data.forEach(user => addOrUpdateUser(user));
    };

    const addOrUpdateUserConnection = (connection: HubConnectionViewObject) => {
        if (user.value && user.value.id === connection.userId) {
            if (!user.value.activeConnections?.length) {
                user.value.activeConnections = [connection];
            } else {
                const existingConnection = user.value.activeConnections.find(x => x.connectionId === connection.connectionId);
                if (existingConnection) {
                    mergeObject(existingConnection, connection);
                } else {
                    user.value.activeConnections.push(connection);
                }
            }
        }
    };

    const removeUserConnection = (userId: string, connectionId: string) => {
        if (user.value && user.value.id === userId) {
            if (user.value.activeConnections?.length) {
                const index = user.value.activeConnections.findIndex(x => x.connectionId === connectionId);
                if (index >= 0) {
                    user.value.activeConnections.splice(index, 1);
                }
            }
        }
    };

    const removeUser = (userId: string) => {
        if (user.value && user.value.id === userId) {
            user.value = null;
        }
    };

    watch(userId, async() => {
        if (!userId.value) {
            addOrUpdateUser();
            return;
        }

        if (userId.value === userStore.user.value?.id) {
            addOrUpdateUser(userStore.user.value);
            return;
        }

        const result = await userService.requestData(userId.value);
        addOrUpdateUser(result);
    }, { immediate: true });

    bus.on('UserUpdated', addOrUpdateUser);
    bus.on('UsersUpdated', addOrUpdateUsers);
    bus.on('UserCreated', addOrUpdateUser);
    bus.on('UserRemoved', removeUser);
    bus.on('OnConnected', addOrUpdateUserConnection);
    bus.on('OnDisconnected', removeUserConnection);
    bus.on('OnConnectionUpdate', addOrUpdateUserConnection);

    onBeforeUnmount(() => {
        bus.off('UserUpdated', addOrUpdateUser);
        bus.off('UsersUpdated', addOrUpdateUsers);
        bus.off('UserCreated', addOrUpdateUser);
        bus.off('UserRemoved', removeUser);
        bus.off('OnConnected', addOrUpdateUserConnection);
        bus.off('OnDisconnected', removeUserConnection);
        bus.off('OnConnectionUpdate', addOrUpdateUserConnection);
    });
    
    return {
        user,
    };
}

export function useUsers(userIds?: Ref<string[]> | null, employeesOnly = false, onInitialized?: () => void) {
    const usersMap = ref<Map<string, UserViewObject>>(new Map<string, UserViewObject>());
    const users = computed(() => Array.from(usersMap.value.values()));

    const addOrUpdateUser = (user: UserViewObject) => {
        if (employeesOnly && user.role === UserRole.None) {
            removeUser(user.id);
            return;
        }

        const existing = usersMap.value.get(user.id);
        if (existing) {
            mergeObject(existing, user);
        } else {
            usersMap.value.set(user.id, user);
        }
    };

    const addOrUpdateUsers = (data: UserViewObject[]) => {
        usersMap.value = employeesOnly 
            ? new Map(data.filter(u => u.role !== UserRole.None).map(user => [user.id, user]))
            : new Map(data.map(user => [user.id, user])); 
    };

    const addOrUpdateUserConnection = (connection: HubConnectionViewObject) => {
        const user = usersMap.value.get(connection.userId);
        if (user) {
            if (!user.activeConnections?.length) {
                user.activeConnections = [connection];
            } else {
                const existingConnection = user.activeConnections.find(x => x.connectionId === connection.connectionId);
                if (existingConnection) {
                    mergeObject(existingConnection, connection);
                } else {
                    user.activeConnections.push(connection);
                }
            }
        }
    };

    const removeUserConnection = (userId: string, connectionId: string) => {
        const user = usersMap.value.get(userId);
        if (user) {
            if (user.activeConnections?.length) {
                const index = user.activeConnections.findIndex(x => x.connectionId === connectionId);
                if (index >= 0) {
                    user.activeConnections.splice(index, 1);
                }
            }
        }
    };

    const removeUser = (userId: string) => {
        usersMap.value.delete(userId);
    };

    if (userIds) {
        watch(userIds, async() => {
            if (userIds.value.length === 0) {
                usersMap.value.clear();
                return;
            }
            const result = await usersApiService.getUsersById({ userIds: userIds.value });
            addOrUpdateUsers(result?.users ?? []);
            onInitialized?.();
        }, { immediate: true });
    } else {
        if (employeesOnly) {
            usersApiService.getEmployees().then(result => {
                addOrUpdateUsers(result?.users ?? []);
                onInitialized?.();
            });
        } else {
            usersApiService.getUsers().then(result => {
                addOrUpdateUsers(result?.users ?? []);
                onInitialized?.();
            });
        }
    }

    bus.on('UserUpdated', addOrUpdateUser);
    bus.on('UsersUpdated', addOrUpdateUsers);
    bus.on('UserCreated', addOrUpdateUser);
    bus.on('UserRemoved', removeUser);
    bus.on('OnConnected', addOrUpdateUserConnection);
    bus.on('OnDisconnected', removeUserConnection);
    bus.on('OnConnectionUpdate', addOrUpdateUserConnection);

    onBeforeUnmount(() => {
        bus.off('UserUpdated', addOrUpdateUser);
        bus.off('UsersUpdated', addOrUpdateUsers);
        bus.off('UserCreated', addOrUpdateUser);
        bus.off('UserRemoved', removeUser);
        bus.off('OnConnected', addOrUpdateUserConnection);
        bus.off('OnDisconnected', removeUserConnection);
        bus.off('OnConnectionUpdate', addOrUpdateUserConnection);
    });
    
    return {
        users,
        usersMap,
    };
}

export function useEmployees() {
    return useUsers(null, true);
}